import { useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Sparkles, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MODELS } from "@/components/tools/toolsConfig";
import { ToolField } from "@/components/tools/ToolField";
import { EmailGateDialog } from "@/components/tools/EmailGateDialog";
import { streamGeneration } from "@/components/tools/stream";
import { getLead } from "@/lib/api";

const initial = (tool) => Object.fromEntries(tool.fields.map((f) => [f.name, f.type === "select" ? f.options[0] : ""]));

export const ToolPanel = ({ tool }) => {
  const [fields, setFields] = useState(() => initial(tool));
  const [model, setModel] = useState("claude");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const pending = useRef(null);

  const run = async (lead = getLead()) => {
    setLoading(true);
    try {
      const r = await streamGeneration({ tool: tool.id, model, fields, email: lead?.email, onStart: () => setOutput(""), onDelta: (d) => setOutput((o) => o + d) });
      if (r.gated) { pending.current = "run"; setGateOpen(true); }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    if (!getLead()) { pending.current = "copy"; setGateOpen(true); return; }
    navigator.clipboard?.writeText(output);
    toast.success("Texto copiado");
  };

  const onUnlocked = (lead) => {
    if (pending.current === "run") run(lead);
    if (pending.current === "copy") { navigator.clipboard?.writeText(output); toast.success("Texto copiado"); }
    pending.current = null;
  };

  const submit = (e) => { e.preventDefault(); run(); };

  return (
    <div className="grid gap-6 lg:grid-cols-12">
      <form onSubmit={submit} className="space-y-5 rounded-2xl border border-[#E8E1D7] bg-[#FAF7F2] p-6 sm:p-8 lg:col-span-5">
        <p className="text-sm text-[#6E655F]">{tool.desc}</p>
        {tool.fields.map((f) => (
          <ToolField key={f.name} field={f} value={fields[f.name]} onChange={(v) => setFields((s) => ({ ...s, [f.name]: v }))} />
        ))}
        <div className="space-y-2">
          <span className="text-sm font-medium text-[#1E1A17]">Modelo de IA</span>
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger data-testid="ai-tool-select-model" className="h-11 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MODELS.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`model-option-${m.id}`}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={loading} data-testid="ai-tool-generate-button" className="h-12 w-full rounded-full bg-[#E05A47] text-base hover:bg-[#c94a38] active:scale-[0.98]">
          <Wand2 className="mr-2 h-4 w-4" /> {loading ? "Creando…" : "Generar con IA"}
        </Button>
      </form>
      <div className="flex min-h-[380px] flex-col rounded-2xl border border-[#E8E1D7] bg-white p-6 sm:p-8 lg:col-span-7">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#2A9D8F]">
            <Sparkles className="h-3.5 w-3.5" /> Resultado
          </span>
          {output && !loading && (
            <Button variant="outline" size="sm" onClick={copy} data-testid="ai-tool-copy-button" className="rounded-full">
              <Copy className="mr-1.5 h-3.5 w-3.5" /> Copiar
            </Button>
          )}
        </div>
        <div data-testid="ai-tool-output" className={`mt-4 flex-1 whitespace-pre-wrap text-[15px] leading-relaxed text-[#1E1A17] ${loading ? "caret" : ""}`}>
          {output || (!loading && <span className="text-[#6E655F]">Rellena los datos de tu negocio y pulsa “Generar con IA”. Tu primer resultado es gratis.</span>)}
        </div>
        {output && !loading && !getLead() && (
          <div data-testid="ai-tool-unlock-banner" className="mt-6 flex flex-col gap-3 rounded-xl bg-[#F4A261]/15 p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-[#1E1A17]">¿Te gusta? Deja tu email para copiarlo y crear sin límite.</p>
            <Button size="sm" onClick={() => setGateOpen(true)} data-testid="ai-tool-unlock-button" className="rounded-full bg-[#1E1A17] hover:bg-[#E05A47]">Desbloquear gratis</Button>
          </div>
        )}
      </div>
      <EmailGateDialog open={gateOpen} onOpenChange={setGateOpen} tool={tool.id} onUnlocked={onUnlocked} />
    </div>
  );
};
